/**
 * MinistrareIA - Follow-ups Page
 * Manage follow-up challenge sequences (e.g., 21-day diet challenge)
 * Supports creating, editing, testing, and activating follow-up sequences.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Bell, Plus, Search, ChevronLeft, ChevronRight, Clock,
  Play, Pause, Trash2, X, Users, FileText,
  Image, Video, Music, File, Upload, Check,
  Loader2, Calendar, ChevronDown, ChevronUp,
  PanelLeftOpen, PanelLeftClose, Sun, Moon, MessageSquare,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import followupService from '../../services/global/followupService';
import fileUploadService from '../../services/global/fileUploadService';
import whatsappService from '../../services/wa-personal/whatsappService';
import waBusinessService from '../../services/wa-business/waBusinessService';
import templateService from '../../services/global/templateService';
import videoLinkService from '../../services/global/videoLinkService';
import DetailsTab from '../../components/followups/DetailsTab';
import RecipientsTab from '../../components/followups/RecipientsTab';
import HistoryTab from '../../components/followups/HistoryTab';




// ============================================================
// HELPER: Empty State
// ============================================================
const EmptyState = ({ colors, onCreateNew }) => (
  <div style={{
    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexDirection: 'column', gap: '16px', padding: '40px',
  }}>
    <div style={{
      width: '80px', height: '80px', borderRadius: '20px',
      backgroundColor: `${colors.accentPrimary}15`, display: 'flex',
      alignItems: 'center', justifyContent: 'center',
    }}>
      <Bell size={36} color={colors.accentPrimary} />
    </div>
    <h3 style={{ fontSize: '18px', fontWeight: 600, color: colors.textPrimary, margin: 0 }}>
      Nenhum follow-up criado
    </h3>
    <p style={{ fontSize: '14px', color: colors.textSecondary, textAlign: 'center', maxWidth: '360px', margin: 0 }}>
      Crie sequencias de mensagens para enviar automaticamente aos seus contatos ao longo dos dias.
    </p>
    <button
      onClick={onCreateNew}
      style={{
        display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 20px',
        backgroundColor: colors.accentPrimary, color: 'white', border: 'none',
        borderRadius: '8px', fontSize: '14px', fontWeight: 500, cursor: 'pointer',
      }}
    >
      <Plus size={18} /> Novo Follow-up
    </button>
  </div>
);


// ============================================================
// HELPER: Status Badge
// ============================================================
const StatusBadge = ({ status, colors }) => {
  const config = {
    draft: { label: 'Rascunho', bg: `${colors.textSecondary}20`, color: colors.textSecondary },
    active: { label: 'Ativo', bg: '#22c55e20', color: '#22c55e' },
    paused: { label: 'Pausado', bg: '#f59e0b20', color: '#f59e0b' },
  };
  const c = config[status] || config.draft;
  return (
    <span style={{
      padding: '3px 10px', borderRadius: '12px', fontSize: '11px',
      fontWeight: 600, backgroundColor: c.bg, color: c.color,
    }}>
      {c.label}
    </span>
  );
};


// ============================================================
// HELPER: File type icon
// ============================================================
const FileTypeIcon = ({ type, size = 16 }) => {
  switch (type) {
    case 'image': return <Image size={size} />;
    case 'video': return <Video size={size} />;
    case 'audio': return <Music size={size} />;
    default: return <File size={size} />;
  }
};


// ============================================================
// SUB: Day Card (week view)
// ============================================================
const DayCard = ({ dayNumber, totalDays, messages, colors, onClick }) => {
  const isEnabled = dayNumber <= totalDays;
  const msgCount = messages.length;
  const hasFiles = messages.some(m => m.files && m.files.length > 0);
  const firstMsg = messages[0];

  return (
    <div
      onClick={isEnabled ? onClick : undefined}
      style={{
        backgroundColor: isEnabled ? colors.bgPrimary : `${colors.bgTertiary}50`,
        border: `1px solid ${isEnabled ? colors.borderColor : 'transparent'}`,
        borderRadius: '12px', padding: '12px', minHeight: '120px',
        cursor: isEnabled ? 'pointer' : 'default',
        opacity: isEnabled ? 1 : 0.4,
        transition: 'all 0.15s',
        display: 'flex', flexDirection: 'column', gap: '8px',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{
          width: '28px', height: '28px', borderRadius: '50%',
          backgroundColor: msgCount > 0 ? colors.accentPrimary : `${colors.textSecondary}20`,
          color: msgCount > 0 ? 'white' : colors.textSecondary,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '12px', fontWeight: 600,
        }}>
          {dayNumber}
        </span>
        {msgCount > 0 && (
          <span style={{
            fontSize: '11px', color: colors.accentPrimary, fontWeight: 600,
            display: 'flex', alignItems: 'center', gap: '3px',
          }}>
            <MessageSquare size={12} /> {msgCount}
          </span>
        )}
      </div>

      {firstMsg ? (
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <p style={{
            fontSize: '11px', color: colors.textSecondary, margin: 0,
            display: 'flex', alignItems: 'center', gap: '4px',
          }}>
            <Clock size={10} /> {firstMsg.send_time}
          </p>
          {firstMsg.text && (
            <p style={{
              fontSize: '12px', color: colors.textPrimary, margin: '4px 0 0',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {firstMsg.text.slice(0, 40)}{firstMsg.text.length > 40 ? '...' : ''}
            </p>
          )}
          {hasFiles && (
            <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
              {messages.flatMap(m => m.files || []).slice(0, 3).map((f, i) => (
                <span key={i} style={{ color: colors.textSecondary }}>
                  <FileTypeIcon type={f.type} size={12} />
                </span>
              ))}
            </div>
          )}
        </div>
      ) : isEnabled ? (
        <p style={{ fontSize: '11px', color: colors.textTertiary, margin: 0, fontStyle: 'italic' }}>
          Clique para configurar
        </p>
      ) : null}
    </div>
  );
};


// ============================================================
// SUB: Day Editor Modal
// ============================================================
const DayEditorModal = ({ isOpen, dayNumber, messages, onSave, onClose, colors, isBusinessChannel, approvedTemplates = [] }) => {
  const [localMessages, setLocalMessages] = useState([]);
  const [uploading, setUploading] = useState({});
  const [videoLinks, setVideoLinks] = useState([]);
  const fileInputRefs = useRef({});

  useEffect(() => {
    if (isOpen) {
      setLocalMessages(messages.map(m => ({ ...m, files: [...(m.files || [])] })));
      videoLinkService.list().then(res => {
        const ready = (res.data || []).filter(v => v.status === 'ready');
        setVideoLinks(ready);
      }).catch(() => setVideoLinks([]));
    }
  }, [isOpen, messages]);

  if (!isOpen) return null;

  const addMessage = () => {
    if (localMessages.length >= 20) return;
    setLocalMessages(prev => [...prev, {
      id: null,
      order: prev.length + 1,
      send_time: '10:00',
      text: '',
      files: [],
      message_type: 'custom',
      video_link_id: '',
      template_name: '',
      template_language: 'pt_BR',
      interactive_type: 'button',
      buttons: [],
      list_button_text: 'Ver opções',
      list_sections: [{ title: 'Opções', rows: [] }],
      header_text: '',
      footer_text: '',
    }]);
  };

  const updateMessage = (index, field, value) => {
    setLocalMessages(prev => prev.map((m, i) => i === index ? { ...m, [field]: value } : m));
  };

  const removeMessage = (index) => {
    setLocalMessages(prev => {
      const updated = prev.filter((_, i) => i !== index);
      return updated.map((m, i) => ({ ...m, order: i + 1 }));
    });
  };

  const moveMessage = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= localMessages.length) return;
    setLocalMessages(prev => {
      const arr = [...prev];
      [arr[index], arr[newIndex]] = [arr[newIndex], arr[index]];
      return arr.map((m, i) => ({ ...m, order: i + 1 }));
    });
  };

  const handleFileUpload = async (msgIndex, files) => {
    const uploadKey = `msg-${msgIndex}`;
    setUploading(prev => ({ ...prev, [uploadKey]: true }));

    try {
      for (const file of files) {
        const validation = fileUploadService.validateFile(file);
        if (!validation.valid) continue;

        const result = await fileUploadService.uploadFile(file);
        if (result) {
          setLocalMessages(prev => prev.map((m, i) => {
            if (i !== msgIndex) return m;
            return {
              ...m,
              files: [...m.files, {
                url: result.url,
                type: result.type,
                filename: result.filename,
                mimeType: result.mimeType,
              }],
            };
          }));
        }
      }
    } finally {
      setUploading(prev => ({ ...prev, [uploadKey]: false }));
    }
  };

  const removeFile = (msgIndex, fileIndex) => {
    setLocalMessages(prev => prev.map((m, i) => {
      if (i !== msgIndex) return m;
      return { ...m, files: m.files.filter((_, fi) => fi !== fileIndex) };
    }));
  };

  const handleSave = () => {
    // Validate: each message must have content based on type
    const valid = localMessages.every(m => {
      if (m.message_type === 'template') return m.template_name && m.template_name.trim();
      if (m.message_type === 'video_link_security') return m.video_link_id && m.video_link_id.trim();
      if (m.message_type === 'interactive') {
        if (!m.text || !m.text.trim()) return false;
        if (m.interactive_type === 'button') {
          return m.buttons && m.buttons.length >= 1 && m.buttons.length <= 3
            && m.buttons.every(b => b.title && b.title.trim() && b.title.length <= 20);
        } else if (m.interactive_type === 'list') {
          const allRows = (m.list_sections || []).flatMap(s => s.rows || []);
          return allRows.length >= 1 && allRows.length <= 10
            && allRows.every(r => r.title && r.title.trim() && r.title.length <= 24);
        }
        return false;
      }
      return (m.text && m.text.trim()) || (m.files && m.files.length > 0);
    });
    if (!valid && localMessages.length > 0) {
      alert('Verifique as mensagens: custom precisa de texto/arquivo, template precisa estar selecionado, interativa precisa de texto + botões/opções válidos, video link precisa estar selecionado.');
      return;
    }
    // Auto-fill defaults and strip UI-only fields before saving
    const cleaned = localMessages.map(m => {
      const msg = { ...m };
      delete msg._showAdvanced;
      if (msg.message_type === 'interactive' && msg.interactive_type === 'list') {
        if (!msg.list_button_text || !msg.list_button_text.trim()) {
          msg.list_button_text = 'Ver opções';
        }
        // Ensure first section has a title
        if (msg.list_sections && msg.list_sections[0] && !msg.list_sections[0].title) {
          msg.list_sections[0].title = 'Opções';
        }
      }
      return msg;
    });
    onSave(cleaned);
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '20px',
    }}>
      <div style={{
        backgroundColor: colors.bgPrimary, borderRadius: '16px',
        width: '100%', maxWidth: '640px', maxHeight: '85vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: `1px solid ${colors.borderColor}`,
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: colors.textPrimary }}>
              Dia {dayNumber} - Mensagens
            </h3>
            <p style={{ margin: 0, fontSize: '12px', color: colors.textSecondary }}>
              {localMessages.length}/20 mensagens configuradas
            </p>
          </div>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer', color: colors.textSecondary,
            padding: '8px', borderRadius: '8px',
          }}>
            <X size={20} />
          </button>
        </div>

        {/* Messages list */}
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {localMessages.length === 0 && (
            <p style={{ color: colors.textSecondary, fontSize: '14px', textAlign: 'center', padding: '24px 0' }}>
              Nenhuma mensagem configurada para este dia.
            </p>
          )}

          {localMessages.map((msg, idx) => (
            <div key={idx} style={{
              backgroundColor: colors.bgSecondary, borderRadius: '12px', padding: '14px',
              border: `1px solid ${colors.borderColor}`,
              overflow: 'hidden', wordBreak: 'break-word', minWidth: 0,
              flexShrink: 0,
            }}>
              {/* Message header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '12px', fontWeight: 600, color: colors.textSecondary }}>
                    Msg {idx + 1}
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                    <Clock size={14} color={colors.textSecondary} />
                    <input
                      type="time"
                      value={msg.send_time}
                      onChange={(e) => updateMessage(idx, 'send_time', e.target.value)}
                      style={{
                        backgroundColor: colors.bgPrimary, color: colors.textPrimary,
                        border: `1px solid ${colors.borderColor}`, borderRadius: '6px',
                        padding: '4px 8px', fontSize: '13px', outline: 'none',
                      }}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <button onClick={() => moveMessage(idx, -1)} disabled={idx === 0} style={{
                    background: 'none', border: 'none', cursor: idx === 0 ? 'default' : 'pointer',
                    color: idx === 0 ? colors.textTertiary : colors.textSecondary, padding: '4px',
                  }}>
                    <ChevronUp size={16} />
                  </button>
                  <button onClick={() => moveMessage(idx, 1)} disabled={idx === localMessages.length - 1} style={{
                    background: 'none', border: 'none',
                    cursor: idx === localMessages.length - 1 ? 'default' : 'pointer',
                    color: idx === localMessages.length - 1 ? colors.textTertiary : colors.textSecondary, padding: '4px',
                  }}>
                    <ChevronDown size={16} />
                  </button>
                  <button onClick={() => removeMessage(idx)} style={{
                    background: 'none', border: 'none', cursor: 'pointer',
                    color: '#ef4444', padding: '4px',
                  }}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {/* Message Type Toggle */}
              <div style={{ display: 'flex', gap: '4px', marginBottom: '10px', flexWrap: 'wrap' }}>
                <button
                  onClick={() => updateMessage(idx, 'message_type', 'custom')}
                  style={{
                    flex: 1, padding: '6px 12px', borderRadius: '6px', fontSize: '12px',
                    fontWeight: 500, cursor: 'pointer', border: 'none', minWidth: 'fit-content',
                    backgroundColor: (msg.message_type || 'custom') === 'custom' ? colors.accentPrimary : colors.bgPrimary,
                    color: (msg.message_type || 'custom') === 'custom' ? '#fff' : colors.textSecondary,
                  }}
                >
                  Custom
                </button>
                {isBusinessChannel && (
                  <>
                    <button
                      onClick={() => updateMessage(idx, 'message_type', 'template')}
                      style={{
                        flex: 1, padding: '6px 12px', borderRadius: '6px', fontSize: '12px',
                        fontWeight: 500, cursor: 'pointer', border: 'none', minWidth: 'fit-content',
                        backgroundColor: msg.message_type === 'template' ? colors.accentPrimary : colors.bgPrimary,
                        color: msg.message_type === 'template' ? '#fff' : colors.textSecondary,
                      }}
                    >
                      Template
                    </button>
                    <button
                      onClick={() => updateMessage(idx, 'message_type', 'interactive')}
                      style={{
                        flex: 1, padding: '6px 12px', borderRadius: '6px', fontSize: '12px',
                        fontWeight: 500, cursor: 'pointer', border: 'none', minWidth: 'fit-content',
                        backgroundColor: msg.message_type === 'interactive' ? colors.accentPrimary : colors.bgPrimary,
                        color: msg.message_type === 'interactive' ? '#fff' : colors.textSecondary,
                      }}
                    >
                      Interativa
                    </button>
                  </>
                )}
                <button
                  onClick={() => updateMessage(idx, 'message_type', 'video_link_security')}
                  style={{
                    flex: 1, padding: '6px 12px', borderRadius: '6px', fontSize: '12px',
                    fontWeight: 500, cursor: 'pointer', border: 'none', minWidth: 'fit-content',
                    backgroundColor: msg.message_type === 'video_link_security' ? '#8b5cf6' : colors.bgPrimary,
                    color: msg.message_type === 'video_link_security' ? '#fff' : colors.textSecondary,
                  }}
                >
                  Video Link
                </button>
              </div>

              {/* Template selector (when template mode) */}
              {isBusinessChannel && msg.message_type === 'template' ? (
                <div>
                  <select
                    value={msg.template_name || ''}
                    onChange={(e) => updateMessage(idx, 'template_name', e.target.value)}
                    style={{
                      width: '100%', backgroundColor: colors.bgPrimary, color: colors.textPrimary,
                      border: `1px solid ${colors.borderColor}`, borderRadius: '8px',
                      padding: '10px', fontSize: '13px', outline: 'none',
                    }}
                  >
                    <option value="">Selecione um template...</option>
                    {approvedTemplates.map(t => (
                      <option key={t.name} value={t.name}>{t.name} ({t.language})</option>
                    ))}
                  </select>
                  {approvedTemplates.length === 0 && (
                    <p style={{ fontSize: '11px', color: '#f59e0b', marginTop: '4px' }}>
                      Nenhum template aprovado. Crie templates na aba Templates.
                    </p>
                  )}
                </div>
              ) : isBusinessChannel && msg.message_type === 'interactive' ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {/* Sub-type toggle: Botões vs Lista */}
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button
                      onClick={() => updateMessage(idx, 'interactive_type', 'button')}
                      style={{
                        flex: 1, padding: '5px 10px', borderRadius: '6px', fontSize: '11px',
                        fontWeight: 500, cursor: 'pointer', border: `1px solid ${colors.borderColor}`,
                        backgroundColor: (msg.interactive_type || 'button') === 'button' ? colors.accentPrimary : colors.bgPrimary,
                        color: (msg.interactive_type || 'button') === 'button' ? '#fff' : colors.textSecondary,
                      }}
                    >
                      Botões (max 3)
                    </button>
                    <button
                      onClick={() => updateMessage(idx, 'interactive_type', 'list')}
                      style={{
                        flex: 1, padding: '5px 10px', borderRadius: '6px', fontSize: '11px',
                        fontWeight: 500, cursor: 'pointer', border: `1px solid ${colors.borderColor}`,
                        backgroundColor: msg.interactive_type === 'list' ? colors.accentPrimary : colors.bgPrimary,
                        color: msg.interactive_type === 'list' ? '#fff' : colors.textSecondary,
                      }}
                    >
                      Lista/Menu (max 10)
                    </button>
                  </div>

                  {/* Body text (required) */}
                  <div>
                    <textarea
                      value={msg.text || ''}
                      onChange={(e) => updateMessage(idx, 'text', e.target.value)}
                      placeholder="Texto da mensagem (obrigatório)"
                      rows={3}
                      maxLength={1024}
                      style={{
                        width: '100%', backgroundColor: colors.bgPrimary, color: colors.textPrimary,
                        border: `1px solid ${colors.borderColor}`, borderRadius: '8px',
                        padding: '10px', fontSize: '13px', resize: 'vertical', outline: 'none',
                        fontFamily: 'inherit', boxSizing: 'border-box',
                        wordBreak: 'break-word', overflowWrap: 'break-word',
                      }}
                    />
                    <span style={{ fontSize: '10px', color: colors.textTertiary }}>{(msg.text || '').length}/1024</span>
                  </div>

                  {/* ===== BUTTON EDITOR ===== */}
                  {(msg.interactive_type || 'button') === 'button' && (
                    <div style={{
                      border: `1px solid ${colors.borderColor}`, borderRadius: '8px',
                      padding: '10px', backgroundColor: colors.bgPrimary,
                    }}>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: colors.textSecondary, marginBottom: '8px' }}>
                        Botões de Resposta ({(msg.buttons || []).length}/3)
                      </div>
                      {(msg.buttons || []).map((btn, bi) => (
                        <div key={bi} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                          <input
                            value={btn.title || ''}
                            onChange={(e) => {
                              const newButtons = [...(msg.buttons || [])];
                              newButtons[bi] = { ...newButtons[bi], title: e.target.value };
                              updateMessage(idx, 'buttons', newButtons);
                            }}
                            placeholder={`Botão ${bi + 1}`}
                            maxLength={20}
                            style={{
                              flex: 1, backgroundColor: colors.bgSecondary, color: colors.textPrimary,
                              border: `1px solid ${colors.borderColor}`, borderRadius: '6px',
                              padding: '6px 8px', fontSize: '12px', outline: 'none',
                            }}
                          />
                          <span style={{ fontSize: '10px', color: colors.textTertiary, minWidth: '30px' }}>
                            {(btn.title || '').length}/20
                          </span>
                          <button
                            onClick={() => {
                              const newButtons = (msg.buttons || []).filter((_, i) => i !== bi);
                              updateMessage(idx, 'buttons', newButtons);
                            }}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '2px' }}
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                      {(msg.buttons || []).length < 3 && (
                        <button
                          onClick={() => {
                            const newButtons = [...(msg.buttons || []), { id: `btn_${(msg.buttons || []).length + 1}`, title: '' }];
                            updateMessage(idx, 'buttons', newButtons);
                          }}
                          style={{
                            display: 'flex', alignItems: 'center', gap: '4px', backgroundColor: 'transparent',
                            color: colors.accentPrimary, border: `1px dashed ${colors.accentPrimary}40`,
                            borderRadius: '6px', padding: '5px 10px', fontSize: '11px', cursor: 'pointer', width: '100%',
                            justifyContent: 'center',
                          }}
                        >
                          <Plus size={12} /> Adicionar botão
                        </button>
                      )}
                    </div>
                  )}

                  {/* ===== LIST EDITOR (Simplified) ===== */}
                  {msg.interactive_type === 'list' && (() => {
                    const sections = msg.list_sections || [{ title: 'Opções', rows: [] }];
                    const allRows = sections[0]?.rows || [];
                    const totalRows = sections.reduce((sum, s) => sum + (s.rows || []).length, 0);
                    return (
                      <div style={{
                        border: `1px solid ${colors.borderColor}`, borderRadius: '8px',
                        padding: '10px', backgroundColor: colors.bgPrimary,
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <span style={{ fontSize: '12px', fontWeight: 600, color: colors.textSecondary }}>
                            Opções do Menu ({totalRows}/10)
                          </span>
                        </div>

                        {/* Simplified rows - title only */}
                        {allRows.map((row, ri) => (
                          <div key={ri} style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                            <input
                              value={row.title || ''}
                              onChange={(e) => {
                                const newSections = [...sections];
                                const newRows = [...(newSections[0].rows || [])];
                                newRows[ri] = { ...newRows[ri], title: e.target.value };
                                newSections[0] = { ...newSections[0], rows: newRows };
                                updateMessage(idx, 'list_sections', newSections);
                              }}
                              placeholder={`Opção ${ri + 1}`}
                              maxLength={24}
                              style={{
                                flex: 1, backgroundColor: colors.bgSecondary, color: colors.textPrimary,
                                border: `1px solid ${colors.borderColor}`, borderRadius: '6px',
                                padding: '6px 8px', fontSize: '12px', outline: 'none',
                              }}
                            />
                            <button
                              onClick={() => {
                                const newSections = [...sections];
                                const newRows = (newSections[0].rows || []).filter((_, i) => i !== ri);
                                newSections[0] = { ...newSections[0], rows: newRows };
                                updateMessage(idx, 'list_sections', newSections);
                              }}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '2px' }}
                            >
                              <X size={14} />
                            </button>
                          </div>
                        ))}

                        {totalRows < 10 && (
                          <button
                            onClick={() => {
                              const newSections = [...sections];
                              if (!newSections[0]) newSections[0] = { title: 'Opções', rows: [] };
                              const newRows = [...(newSections[0].rows || []), { id: `row_${totalRows + 1}`, title: '', description: '' }];
                              newSections[0] = { ...newSections[0], rows: newRows };
                              updateMessage(idx, 'list_sections', newSections);
                            }}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '4px', backgroundColor: 'transparent',
                              color: colors.accentPrimary, border: `1px dashed ${colors.accentPrimary}40`,
                              borderRadius: '6px', padding: '5px 10px', fontSize: '11px', cursor: 'pointer', width: '100%',
                              justifyContent: 'center',
                            }}
                          >
                            <Plus size={12} /> Adicionar opção
                          </button>
                        )}
                      </div>
                    );
                  })()}

                  {/* ===== ADVANCED OPTIONS TOGGLE ===== */}
                  <div>
                    <button
                      onClick={() => updateMessage(idx, '_showAdvanced', !msg._showAdvanced)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '4px', backgroundColor: 'transparent',
                        color: colors.textTertiary, border: 'none', padding: '4px 0',
                        fontSize: '11px', cursor: 'pointer',
                      }}
                    >
                      {msg._showAdvanced ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                      Opções avançadas
                    </button>
                    {msg._showAdvanced && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '6px',
                        padding: '10px', border: `1px solid ${colors.borderColor}`, borderRadius: '8px',
                        backgroundColor: colors.bgPrimary,
                      }}>
                        <input
                          value={msg.header_text || ''}
                          onChange={(e) => updateMessage(idx, 'header_text', e.target.value)}
                          placeholder="Cabeçalho (opcional, max 60)"
                          maxLength={60}
                          style={{
                            width: '100%', backgroundColor: colors.bgSecondary, color: colors.textPrimary,
                            border: `1px solid ${colors.borderColor}`, borderRadius: '6px',
                            padding: '6px 8px', fontSize: '12px', outline: 'none', boxSizing: 'border-box',
                          }}
                        />
                        <input
                          value={msg.footer_text || ''}
                          onChange={(e) => updateMessage(idx, 'footer_text', e.target.value)}
                          placeholder="Rodapé (opcional, max 60)"
                          maxLength={60}
                          style={{
                            width: '100%', backgroundColor: colors.bgSecondary, color: colors.textPrimary,
                            border: `1px solid ${colors.borderColor}`, borderRadius: '6px',
                            padding: '6px 8px', fontSize: '12px', outline: 'none', boxSizing: 'border-box',
                          }}
                        />
                        {msg.interactive_type === 'list' && (
                          <>
                            <input
                              value={msg.list_button_text || 'Ver opções'}
                              onChange={(e) => updateMessage(idx, 'list_button_text', e.target.value)}
                              placeholder="Texto do botão do menu (max 20)"
                              maxLength={20}
                              style={{
                                width: '100%', backgroundColor: colors.bgSecondary, color: colors.textPrimary,
                                border: `1px solid ${colors.borderColor}`, borderRadius: '6px',
                                padding: '6px 8px', fontSize: '12px', outline: 'none', boxSizing: 'border-box',
                              }}
                            />
                            {/* Row descriptions */}
                            {((msg.list_sections || [])[0]?.rows || []).map((row, ri) => (
                              row.title ? (
                                <div key={ri} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <span style={{ fontSize: '11px', color: colors.textSecondary, minWidth: '80px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {row.title}:
                                  </span>
                                  <input
                                    value={row.description || ''}
                                    onChange={(e) => {
                                      const newSections = [...(msg.list_sections || [])];
                                      const newRows = [...(newSections[0].rows || [])];
                                      newRows[ri] = { ...newRows[ri], description: e.target.value };
                                      newSections[0] = { ...newSections[0], rows: newRows };
                                      updateMessage(idx, 'list_sections', newSections);
                                    }}
                                    placeholder="Descrição (opcional)"
                                    maxLength={72}
                                    style={{
                                      flex: 1, backgroundColor: colors.bgSecondary, color: colors.textPrimary,
                                      border: `1px solid ${colors.borderColor}`, borderRadius: '6px',
                                      padding: '4px 8px', fontSize: '11px', outline: 'none',
                                    }}
                                  />
                                </div>
                              ) : null
                            ))}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : msg.message_type === 'video_link_security' ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  <select
                    value={msg.video_link_id || ''}
                    onChange={(e) => updateMessage(idx, 'video_link_id', e.target.value)}
                    style={{
                      width: '100%', backgroundColor: colors.bgPrimary, color: colors.textPrimary,
                      border: `1px solid ${colors.borderColor}`, borderRadius: '8px',
                      padding: '10px', fontSize: '13px', outline: 'none',
                    }}
                  >
                    <option value="">Selecione um video link...</option>
                    {videoLinks.map(v => (
                      <option key={v.id} value={v.id}>{v.name} (limite: {v.view_limit}x)</option>
                    ))}
                  </select>
                  {videoLinks.length === 0 && (
                    <p style={{ fontSize: '11px', color: '#f59e0b', marginTop: '0' }}>
                      Nenhum video link pronto. Crie na aba Video Links.
                    </p>
                  )}
                  <textarea
                    value={msg.text || ''}
                    onChange={(e) => updateMessage(idx, 'text', e.target.value)}
                    placeholder="Texto acompanhando o link (opcional)"
                    rows={2}
                    style={{
                      width: '100%', backgroundColor: colors.bgPrimary, color: colors.textPrimary,
                      border: `1px solid ${colors.borderColor}`, borderRadius: '8px',
                      padding: '10px', fontSize: '13px', resize: 'vertical', outline: 'none',
                      fontFamily: 'inherit', boxSizing: 'border-box',
                    }}
                  />
                </div>
              ) : (
                <>
                  {/* Text */}
                  <textarea
                    value={msg.text || ''}
                    onChange={(e) => updateMessage(idx, 'text', e.target.value)}
                    placeholder="Texto da mensagem (opcional se tiver arquivo)"
                    rows={3}
                    style={{
                      width: '100%', backgroundColor: colors.bgPrimary, color: colors.textPrimary,
                      border: `1px solid ${colors.borderColor}`, borderRadius: '8px',
                      padding: '10px', fontSize: '13px', resize: 'vertical', outline: 'none',
                      fontFamily: 'inherit', boxSizing: 'border-box',
                      wordBreak: 'break-word', overflowWrap: 'break-word',
                    }}
                  />

                  {/* Files */}
              <div style={{ marginTop: '8px' }}>
                {msg.files.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                    {msg.files.map((f, fi) => (
                      <div key={fi} style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        backgroundColor: colors.bgPrimary, padding: '4px 10px',
                        borderRadius: '6px', fontSize: '12px', color: colors.textSecondary,
                        border: `1px solid ${colors.borderColor}`,
                      }}>
                        <FileTypeIcon type={f.type} size={14} />
                        <span style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {f.filename}
                        </span>
                        <button onClick={() => removeFile(idx, fi)} style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          color: '#ef4444', padding: '2px', display: 'flex',
                        }}>
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <input
                  ref={el => fileInputRefs.current[idx] = el}
                  type="file"
                  multiple
                  accept={fileUploadService.getAcceptString()}
                  onChange={(e) => {
                    if (e.target.files.length > 0) handleFileUpload(idx, Array.from(e.target.files));
                    e.target.value = '';
                  }}
                  style={{ display: 'none' }}
                />
                <button
                  onClick={() => fileInputRefs.current[idx]?.click()}
                  disabled={uploading[`msg-${idx}`]}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '6px',
                    backgroundColor: 'transparent', color: colors.accentPrimary,
                    border: `1px dashed ${colors.accentPrimary}40`, borderRadius: '6px',
                    padding: '6px 12px', fontSize: '12px', cursor: 'pointer', width: '100%',
                    justifyContent: 'center',
                  }}
                >
                  {uploading[`msg-${idx}`] ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                  {uploading[`msg-${idx}`] ? 'Enviando...' : 'Adicionar arquivos'}
                </button>
              </div>
                </>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 20px', borderTop: `1px solid ${colors.borderColor}`,
        }}>
          <button
            onClick={addMessage}
            disabled={localMessages.length >= 20}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px',
              backgroundColor: 'transparent', color: localMessages.length >= 20 ? colors.textTertiary : colors.accentPrimary,
              border: `1px solid ${localMessages.length >= 20 ? colors.borderColor : colors.accentPrimary}`,
              borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: localMessages.length >= 20 ? 'default' : 'pointer',
            }}
          >
            <Plus size={16} /> Mensagem
          </button>
          <button onClick={handleSave} style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            backgroundColor: colors.accentPrimary, color: 'white', border: 'none',
            borderRadius: '8px', padding: '8px 20px', fontSize: '13px', fontWeight: 500, cursor: 'pointer',
          }}>
            <Check size={16} /> Salvar dia
          </button>
        </div>
      </div>
    </div>
  );
};


// ============================================================
// SUB: Contact Picker Modal
// ============================================================
const ContactPickerModal = ({ isOpen, onClose, onSelect, colors, isBusinessChannel }) => {
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      setSelected([]);
      setSearch('');

      const fetchContacts = isBusinessChannel
        ? waBusinessService.getChats(200, 0).then(data => {
            const chatList = data?.data || data || [];
            return Array.isArray(chatList) ? chatList : [];
          })
        : whatsappService.getChats().then(data => {
            const chatList = data?.data || data || [];
            return Array.isArray(chatList) ? chatList : [];
          });

      fetchContacts
        .then(list => setChats(list))
        .catch(() => setChats([]))
        .finally(() => setLoading(false));
    }
  }, [isOpen, isBusinessChannel]);

  if (!isOpen) return null;

  const getNumber = (chat) => {
    if (isBusinessChannel) {
      return chat.contact_phone || '';
    }
    const jid = chat.remoteJid || chat.id || '';
    return jid.replace('@s.whatsapp.net', '').replace('@g.us', '');
  };

  const getName = (chat) => {
    if (isBusinessChannel) {
      return chat.contact_name || chat.contact_phone || '';
    }
    return chat.pushName || chat.name || chat.contactName || getNumber(chat);
  };

  const filtered = chats.filter(c => {
    if (isBusinessChannel) {
      if (!c.contact_phone) return false;
    } else {
      if (!c.remoteJid || c.remoteJid.endsWith('@g.us')) return false;
    }
    const s = search.toLowerCase();
    return !s || getName(c).toLowerCase().includes(s) || getNumber(c).includes(s);
  });

  const toggleSelect = (number) => {
    setSelected(prev => prev.includes(number) ? prev.filter(n => n !== number) : [...prev, number]);
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1001,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px',
    }}>
      <div style={{
        backgroundColor: colors.bgPrimary, borderRadius: '16px',
        width: '100%', maxWidth: '440px', maxHeight: '70vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <div style={{
          padding: '16px 20px', borderBottom: `1px solid ${colors.borderColor}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: colors.textPrimary }}>
            Selecionar contatos
          </h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: colors.textSecondary }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: '12px 20px' }}>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar contato..."
            style={{
              width: '100%', padding: '8px 12px', backgroundColor: colors.bgSecondary,
              color: colors.textPrimary, border: `1px solid ${colors.borderColor}`,
              borderRadius: '8px', fontSize: '13px', outline: 'none', boxSizing: 'border-box',
            }}
          />
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '0 20px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '24px', color: colors.textSecondary }}>
              <Loader2 size={20} /> Carregando...
            </div>
          ) : filtered.length === 0 ? (
            <p style={{ textAlign: 'center', color: colors.textSecondary, fontSize: '13px', padding: '24px' }}>
              Nenhum contato encontrado
            </p>
          ) : filtered.map((chat, i) => {
            const number = getNumber(chat);
            const isSelected = selected.includes(number);
            return (
              <div
                key={i}
                onClick={() => toggleSelect(number)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '12px',
                  padding: '10px 0', borderBottom: `1px solid ${colors.borderColor}20`,
                  cursor: 'pointer',
                }}
              >
                <div style={{
                  width: '20px', height: '20px', borderRadius: '4px',
                  border: `2px solid ${isSelected ? colors.accentPrimary : colors.borderColor}`,
                  backgroundColor: isSelected ? colors.accentPrimary : 'transparent',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {isSelected && <Check size={12} color="white" />}
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ margin: 0, fontSize: '13px', fontWeight: 500, color: colors.textPrimary }}>
                    {getName(chat)}
                  </p>
                  <p style={{ margin: 0, fontSize: '11px', color: colors.textSecondary }}>
                    {number}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        <div style={{
          padding: '14px 20px', borderTop: `1px solid ${colors.borderColor}`,
          display: 'flex', justifyContent: 'flex-end', gap: '8px',
        }}>
          <button onClick={onClose} style={{
            padding: '8px 16px', backgroundColor: 'transparent',
            color: colors.textSecondary, border: `1px solid ${colors.borderColor}`,
            borderRadius: '8px', fontSize: '13px', cursor: 'pointer',
          }}>
            Cancelar
          </button>
          <button
            onClick={() => { onSelect(selected); onClose(); }}
            disabled={selected.length === 0}
            style={{
              padding: '8px 16px', backgroundColor: selected.length === 0 ? colors.borderColor : colors.accentPrimary,
              color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px',
              fontWeight: 500, cursor: selected.length === 0 ? 'default' : 'pointer',
            }}
          >
            Adicionar {selected.length > 0 ? `(${selected.length})` : ''}
          </button>
        </div>
      </div>
    </div>
  );
};


// ============================================================
// MAIN COMPONENT
// ============================================================

const FollowUpsPage = ({ isNavOpen, onToggleNav }) => {
  const { colors, theme, toggleTheme } = useTheme();
  const { user } = useAuth();

  // View management
  const [view, setView] = useState('list'); // list | detail
  const [followups, setFollowups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [editingDay, setEditingDay] = useState(null);

  // Detail view state
  const [selectedFollowup, setSelectedFollowup] = useState(null);
  const [history, setHistory] = useState([]);
  const [testNumber, setTestNumber] = useState('');
  const [sendingDay, setSendingDay] = useState(null);
  const [showContactPicker, setShowContactPicker] = useState(false);
  const [detailTab, setDetailTab] = useState('details');

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // WhatsApp Business templates
  const [approvedTemplates, setApprovedTemplates] = useState([]);

  const isBusinessChannel = user?.default_channel === 'wa_business';

  // ============================================
  // DATA LOADING
  // ============================================

  const loadFollowups = useCallback(async () => {
    setLoading(true);
    try {
      const res = await followupService.list();
      setFollowups(res.data || []);
    } catch (err) {
      console.error('Error loading followups:', err);
      setError('Erro ao carregar follow-ups');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFollowups();
  }, [loadFollowups]);

  // Load approved templates for Business channel
  useEffect(() => {
    if (isBusinessChannel) {
      templateService.list()
        .then(data => {
          const approved = (data.templates || []).filter(t => t.status === 'APPROVED');
          setApprovedTemplates(approved);
        })
        .catch(err => console.error('Error loading templates:', err));
    }
  }, [isBusinessChannel]);

  const loadFollowupDetail = useCallback(async (id) => {
    try {
      const res = await followupService.getById(id);
      setSelectedFollowup(res.data);
      const histRes = await followupService.getHistory(id);
      setHistory(histRes.data || []);
    } catch (err) {
      console.error('Error loading followup detail:', err);
    }
  }, []);

  // ============================================
  // HANDLERS
  // ============================================

  const handleCreateNew = async () => {
    setSaving(true);
    try {
      const payload = {
        name: 'Novo Follow-up',
        description: '',
        total_days: 0,
        recipients: [],
        days: {},
        type: 'challenge_sequence',
        channel: isBusinessChannel ? 'wa_business' : 'wa_personal',
        business_config: isBusinessChannel ? {
          fallback_template_name: '',
          fallback_template_language: 'pt_BR',
          retry_interval_hours: 6,
          max_retries: 3,
        } : null,
      };
      const res = await followupService.create(payload);
      const newId = res.data?.id || res.data;
      await loadFollowupDetail(newId);
      await loadFollowups();
      setDetailTab('details');
      setView('detail');
    } catch (err) {
      console.error('Error creating followup:', err);
      alert('Erro ao criar follow-up: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (followup) => {
    if (!window.confirm(`Excluir "${followup.name}"? Esta acao nao pode ser desfeita.`)) return;
    try {
      await followupService.delete(followup.id);
      await loadFollowups();
    } catch (err) {
      console.error('Error deleting followup:', err);
    }
  };

  const handleStatusToggle = async (followup) => {
    try {
      if (followup.status === 'active') {
        await followupService.updateStatus(followup.id, 'paused');
      } else if (followup.status === 'paused') {
        await followupService.updateStatus(followup.id, 'active');
      }
      await loadFollowups();
    } catch (err) {
      alert('Erro: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleViewDetail = async (followup) => {
    await loadFollowupDetail(followup.id);
    setTestNumber('');
    setDetailTab('details');
    setView('detail');
  };

  // Resumo HONESTO do envio: antes o portal dizia "reenviado com sucesso" mesmo quando o
  // backend tinha pulado tudo (caso real 18/09/2026).
  const resumoEnvio = (day, phone, data) => {
    const enviados = data?.sent?.length || 0;
    const falhas = data?.failed || [];
    const pulados = (data?.skipped || []).length;
    const linhas = [`Dia ${day} para ${phone}:`];
    linhas.push(`${enviados} mensagem(ns) enviada(s) para a Meta.`);
    if (falhas.length) linhas.push(`${falhas.length} com erro: ${falhas.map((f) => f.error).filter(Boolean).slice(0, 2).join(' | ')}`);
    if (pulados) linhas.push(`${pulados} não reenviada(s) (template de saudação).`);
    if (enviados) linhas.push('A confirmação de entrega de cada arquivo aparece no Histórico de envio em alguns segundos.');
    return linhas.join('\n');
  };

  const handleSendDay = async (day) => {
    if (!testNumber.trim()) {
      alert('Informe o numero para teste.');
      return;
    }
    setSendingDay(day);
    try {
      const res = await followupService.sendDay(selectedFollowup.id, day, [testNumber.trim()], true, true);
      alert(resumoEnvio(day, testNumber.trim(), res.data));
      // Refresh history
      const histRes = await followupService.getHistory(selectedFollowup.id);
      setHistory(histRes.data || []);
    } catch (err) {
      alert('Erro: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSendingDay(null);
    }
  };

  const handleResendDay = async (day, phone) => {
    if (!window.confirm(`Reenviar agora todo o conteúdo do Dia ${day} para ${phone}?\nO dia da pessoa no desafio não muda.`)) return;
    try {
      const res = await followupService.sendDay(selectedFollowup.id, day, [phone], false, true);
      alert(resumoEnvio(day, phone, res.data));
      const histRes = await followupService.getHistory(selectedFollowup.id);
      setHistory(histRes.data || []);
    } catch (err) {
      alert('Erro ao reenviar: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleActivate = async () => {
    try {
      await followupService.updateStatus(selectedFollowup.id, 'active');
      alert('Follow-up ativado com sucesso!');
      await loadFollowupDetail(selectedFollowup.id);
      await loadFollowups();
    } catch (err) {
      alert('Erro: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleResetDay = async (day) => {
    if (!window.confirm(
      `Apagar o registro de envio do Dia ${day} para TODOS os participantes?\n\n` +
      'Quem já passou desse dia pode receber o conteúdo de novo. Ninguém volta para o dia zero: ' +
      'o dia de cada pessoa vem da data de inscrição.\n\n' +
      'Para reenviar a UMA pessoa, use "Reenviar este dia" no Histórico de envio.'
    )) return;
    try {
      await followupService.resetHistoryByDay(selectedFollowup.id, day);
      const histRes = await followupService.getHistory(selectedFollowup.id);
      setHistory(histRes.data || []);
    } catch (err) {
      console.error('Error resetting day:', err);
    }
  };

  const handleUpdateFollowup = async (partialData) => {
    try {
      await followupService.update(selectedFollowup.id, partialData);
      await loadFollowupDetail(selectedFollowup.id);
    } catch (err) {
      alert('Erro ao atualizar: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleRemoveRecipient = async (phone) => {
    try {
      await followupService.removeRecipient(selectedFollowup.id, phone);
      await loadFollowupDetail(selectedFollowup.id);
      await loadFollowups();
    } catch (err) {
      alert('Erro ao remover: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDetailDayEditorSave = async (dayNumber, messages) => {
    try {
      const updatedDays = { ...selectedFollowup.days };
      updatedDays[String(dayNumber)] = { messages };
      await followupService.update(selectedFollowup.id, { days: updatedDays });
      await loadFollowupDetail(selectedFollowup.id);
      setEditingDay(null);
    } catch (err) {
      alert('Erro ao salvar dia: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleAddDay = async (newDayNumber) => {
    try {
      await followupService.update(selectedFollowup.id, { total_days: newDayNumber });
      await loadFollowupDetail(selectedFollowup.id);
      setEditingDay(newDayNumber);
    } catch (err) {
      alert('Erro ao adicionar dia: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleAddRecipientsToFollowup = async (numbers) => {
    if (!selectedFollowup) return;
    const existing = selectedFollowup.recipients || [];
    // Extract existing phone numbers (handles both old string and new object format)
    const existingPhones = existing.map(r => typeof r === 'string' ? r : r?.phone || '');
    // Merge: keep existing + add only new numbers (send as phone strings, backend handles enrolled_at)
    const newNumbers = numbers.filter(n => !existingPhones.includes(n));
    const merged = [...existingPhones, ...newNumbers];
    try {
      await followupService.update(selectedFollowup.id, { recipients: merged });
      await loadFollowupDetail(selectedFollowup.id);
      await loadFollowups();
    } catch (err) {
      alert('Erro ao adicionar destinatarios: ' + (err.response?.data?.detail || err.message));
    }
  };

  // Filtered followups
  const filteredFollowups = followups.filter(f => {
    if (statusFilter !== 'all' && f.status !== statusFilter) return false;
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      if (!f.name.toLowerCase().includes(s) && !f.description?.toLowerCase().includes(s)) return false;
    }
    return true;
  });

  // ============================================
  // STYLES
  // ============================================

  const styles = {
    container: {
      flex: 1, display: 'flex', flexDirection: 'column',
      backgroundColor: colors.bgSecondary, height: '100vh', overflow: 'hidden',
    },
    header: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '10px 24px', backgroundColor: colors.headerBg,
      borderBottom: `1px solid ${colors.borderColor}`, minHeight: '60px',
    },
    headerLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
    navToggleBtn: {
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      width: '40px', height: '40px', border: 'none', background: 'transparent',
      color: colors.textSecondary, borderRadius: '8px', cursor: 'pointer',
    },
    headerIcon: {
      width: '40px', height: '40px', backgroundColor: colors.accentPrimary,
      borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white',
    },
    content: { flex: 1, overflow: 'auto', padding: '20px 24px' },
    card: {
      backgroundColor: colors.bgPrimary, borderRadius: '12px', padding: '16px',
      border: `1px solid ${colors.borderColor}`, cursor: 'pointer',
      transition: 'border-color 0.15s',
    },
    btn: {
      display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 16px',
      borderRadius: '8px', fontSize: '13px', fontWeight: 500, cursor: 'pointer', border: 'none',
    },
    btnPrimary: { backgroundColor: colors.accentPrimary, color: 'white' },
    btnOutline: { backgroundColor: 'transparent', color: colors.textSecondary, border: `1px solid ${colors.borderColor}` },
    input: {
      width: '100%', padding: '10px 12px', backgroundColor: colors.bgPrimary,
      color: colors.textPrimary, border: `1px solid ${colors.borderColor}`,
      borderRadius: '8px', fontSize: '14px', outline: 'none', boxSizing: 'border-box',
    },
  };

  // ============================================
  // RENDER: LIST VIEW
  // ============================================

  const renderListView = () => (
    <>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 200px', maxWidth: '300px' }}>
          <Search size={16} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: colors.textSecondary }} />
          <input
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder="Buscar follow-up..."
            style={{ ...styles.input, paddingLeft: '34px' }}
          />
        </div>
        <div style={{ display: 'flex', gap: '4px' }}>
          {['all', 'draft', 'active', 'paused'].map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              style={{
                padding: '6px 12px', borderRadius: '16px', fontSize: '12px', fontWeight: 500,
                border: 'none', cursor: 'pointer',
                backgroundColor: statusFilter === s ? colors.accentPrimary : `${colors.textSecondary}15`,
                color: statusFilter === s ? 'white' : colors.textSecondary,
              }}
            >
              {s === 'all' ? 'Todos' : s === 'draft' ? 'Rascunho' : s === 'active' ? 'Ativo' : 'Pausado'}
            </button>
          ))}
        </div>
      </div>

      {/* Cards grid */}
      {filteredFollowups.length === 0 && !loading ? (
        <EmptyState colors={colors} onCreateNew={handleCreateNew} />
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
          {filteredFollowups.map(f => {
            const daysConfigured = Object.keys(f.days_summary || {}).filter(
              k => (f.days_summary[k]?.message_count || 0) > 0
            ).length;
            return (
              <div key={f.id} style={styles.card} onClick={() => handleViewDetail(f)}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <div>
                    <h4 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: colors.textPrimary }}>
                      {f.name}
                    </h4>
                    {f.description && (
                      <p style={{ margin: '4px 0 0', fontSize: '12px', color: colors.textSecondary }}>
                        {f.description.slice(0, 60)}{f.description.length > 60 ? '...' : ''}
                      </p>
                    )}
                  </div>
                  <StatusBadge status={f.status} colors={colors} />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '12px', color: colors.textSecondary }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Calendar size={13} /> {f.total_days} dias
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <FileText size={13} /> {daysConfigured} configurados
                  </span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <Users size={13} /> {(f.recipients || []).length}
                  </span>
                </div>


                {/* Card actions */}
                <div
                  style={{ display: 'flex', gap: '6px', marginTop: '12px', borderTop: `1px solid ${colors.borderColor}`, paddingTop: '10px' }}
                  onClick={e => e.stopPropagation()}
                >
                  {(f.status === 'active' || f.status === 'paused') && (
                    <button onClick={() => handleStatusToggle(f)} style={{ ...styles.btn, ...styles.btnOutline, padding: '6px 10px', fontSize: '12px' }}>
                      {f.status === 'active' ? <><Pause size={13} /> Pausar</> : <><Play size={13} /> Retomar</>}
                    </button>
                  )}
                  <button onClick={() => handleDelete(f)} style={{ ...styles.btn, padding: '6px 10px', fontSize: '12px', backgroundColor: 'transparent', color: '#ef4444', border: '1px solid #ef444430' }}>
                    <Trash2 size={13} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </>
  );

  // ============================================
  // RENDER: DETAIL VIEW
  // ============================================

  const renderDetailView = () => {
    if (!selectedFollowup) return null;

    const tabs = [
      { key: 'details', label: 'Detalhes' },
      { key: 'recipients', label: 'Destinatarios' },
      { key: 'history', label: 'Historico de envio' },
    ];

    return (
      <>
        {/* Tab Bar (pill style) */}
        <div style={{
          display: 'flex', gap: '6px', marginBottom: '20px',
          overflowX: 'auto', paddingBottom: '2px',
        }}>
          {tabs.map(tab => {
            const isActive = detailTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setDetailTab(tab.key)}
                style={{
                  padding: '7px 14px',
                  border: 'none',
                  backgroundColor: isActive ? colors.accentPrimary : colors.bgTertiary,
                  color: isActive ? 'white' : colors.textSecondary,
                  borderRadius: '18px',
                  fontSize: '14px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  whiteSpace: 'nowrap',
                }}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {detailTab === 'details' && (
          <DetailsTab
            followup={selectedFollowup}
            history={history}
            colors={colors}
            styles={styles}
            testNumber={testNumber}
            setTestNumber={setTestNumber}
            sendingDay={sendingDay}
            onSendDay={handleSendDay}
            onResetDay={handleResetDay}
            onActivate={handleActivate}
            onUpdateFollowup={handleUpdateFollowup}
            onOpenDayEditor={(dayNum) => setEditingDay(dayNum)}
            onAddDay={handleAddDay}
            isBusinessChannel={isBusinessChannel}
            approvedTemplates={approvedTemplates}
          />
        )}

        {detailTab === 'recipients' && (
          <RecipientsTab
            followup={selectedFollowup}
            history={history}
            colors={colors}
            styles={styles}
            onRemoveRecipient={handleRemoveRecipient}
            onRefresh={() => loadFollowupDetail(selectedFollowup.id)}
            onAddRecipients={handleAddRecipientsToFollowup}
            onOpenContactPicker={() => setShowContactPicker(true)}
            onResendDay={handleResendDay}
          />
        )}

        {detailTab === 'history' && (
          <HistoryTab
            history={history}
            colors={colors}
            styles={styles}
            onResendDay={handleResendDay}
          />
        )}
      </>
    );
  };

  // ============================================
  // MAIN RENDER
  // ============================================

  // Business channel placeholder removed — now fully functional

  const getHeaderInfo = () => {
    switch (view) {
      case 'detail': return { title: selectedFollowup?.name || 'Detalhe', subtitle: 'Detalhe e teste' };
      default: return { title: 'Follow-ups', subtitle: `${followups.length} follow-up${followups.length !== 1 ? 's' : ''}` };
    }
  };

  const headerInfo = getHeaderInfo();

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          {onToggleNav && (
            <button onClick={onToggleNav} style={styles.navToggleBtn}>
              {isNavOpen ? <PanelLeftClose size={20} /> : <PanelLeftOpen size={20} />}
            </button>
          )}
          {view !== 'list' && (
            <button
              onClick={() => { setView('list'); loadFollowups(); }}
              style={{ ...styles.navToggleBtn, color: colors.textPrimary }}
            >
              <ChevronLeft size={22} />
            </button>
          )}
          <div style={styles.headerIcon}><Bell size={20} /></div>
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: 600, color: colors.textPrimary, margin: 0 }}>
              {headerInfo.title}
            </h2>
            <p style={{ fontSize: '13px', color: colors.textSecondary, margin: 0 }}>{headerInfo.subtitle}</p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {view === 'list' && (
            <button onClick={handleCreateNew} disabled={saving} style={{ ...styles.btn, ...styles.btnPrimary, opacity: saving ? 0.6 : 1 }}>
              {saving ? <Loader2 size={16} /> : <Plus size={16} />} Novo Follow-up
            </button>
          )}
          <button onClick={toggleTheme} style={styles.navToggleBtn}>
            {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        </div>
      </div>

      {/* Content */}
      <div style={styles.content}>
        {loading && view === 'list' ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px', color: colors.textSecondary }}>
            <Loader2 size={24} /> <span style={{ marginLeft: '8px' }}>Carregando...</span>
          </div>
        ) : (
          <>
            {view === 'list' && renderListView()}
            {view === 'detail' && renderDetailView()}
          </>
        )}
      </div>

      {/* Day Editor Modal */}
      <DayEditorModal
        isOpen={editingDay !== null}
        dayNumber={editingDay}
        messages={editingDay ? (selectedFollowup?.days?.[String(editingDay)]?.messages || []) : []}
        onSave={(messages) => handleDetailDayEditorSave(editingDay, messages)}
        onClose={() => setEditingDay(null)}
        colors={colors}
        isBusinessChannel={isBusinessChannel}
        approvedTemplates={approvedTemplates}
      />

      {/* Contact Picker Modal */}
      <ContactPickerModal
        isOpen={showContactPicker}
        onClose={() => setShowContactPicker(false)}
        onSelect={handleAddRecipientsToFollowup}
        colors={colors}
        isBusinessChannel={isBusinessChannel}
      />
    </div>
  );
};

export default FollowUpsPage;
